import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { RecordType, filterRecordTypesForToolMode } from "../../api/consts";
import {
  useCreateMeasureMutation,
  useCreateRecordMutation,
  useDeleteRecordMutation,
} from "../../api/managementApi";
import { namespaces } from "../../consts/i18n";
import useAuthInfo from "../../hooks/useAuthInfo";
import useUploadRecordFiles from "../../hooks/useUploadRecordFiles";
import { removeNonStoredMeasure } from "../../redux/measures2/measures2Reducer";
import { removeNonStoredRecord } from "../../redux/records/recordsReducer";
import { getErrorForPath } from "../../utils/serverIssuesUtils";
import CloseButton from "../buttons/CloseButton";
import TextButton from "../buttons/TextButton";
import FacilityForm from "../forms/FacilityForm";
import FVEForm from "../forms/FVEForm";
import GeneralForm from "../forms/GeneralForm";
import GraveYardForm from "../forms/GraveYardForm";
import ParksAndGardensForm from "../forms/ParksAndGardensForm";
import CheckboxInput from "../inputs/CheckboxInput";
import TextInput from "../inputs/TextInput";
import TogglesSelect from "../inputs/TogglesSelect";
import LoadingBar from "../LoadingBar";
import DialogTitle from "./DialogTitle";
import DialogWrapper from "./DialogWrapper";
import ServiceForm from "../forms/ServiceForm";

const CreateRecordDialog = ({ isOpen, onClose, initialData }) => {
  const { project } = useSelector((state) => state.info);
  const { mode } = useSelector((state) => state.measures);
  const { t } = useTranslation(namespaces.viewer);
  const dispatch = useDispatch();

  const { canEdit } = useAuthInfo();

  const [recordTypeOptions, setRecordTypeOptions] = useState([]);
  const [formData, setFormData] = useState({});
  const [checkRecordVisible, setCheckRecordVisible] = useState(false);
  const [data, setData] = useState({
    ...initialData,
  });

  const isInitialSetup = useRef(true);

  const [createRecord, { isLoading }] = useCreateRecordMutation();

  const [createMeasure] = useCreateMeasureMutation();

  const [deleteRecord] = useDeleteRecordMutation();

  const [issues, setIssues] = useState(null);

  const {
    uploadFiles,
    isUploading,
    uploadProgress,
    uploadError,
    uploadAbortController,
    clearUploadError,
  } = useUploadRecordFiles();

  const handleCancel = useCallback(() => {
    // cancel upload if in progress
    if (isUploading) {
      uploadAbortController.current.abort();
    }

    onClose();
  }, [onClose, isUploading, uploadAbortController]);

  const handleSubmit = useCallback(async () => {
    const { measure, record } = initialData;
    // omit nore from formData
    const { note, ...rest } = formData;
    let uploadFailed = false;
    if (data.isRecord) {
      let response;

      try {
        response = await createRecord({
          projectId: project.id,
          name: data.name,
          type: data.recordType,
          note: note,
          data: measure.points,
          extra: rest,
          showValues: data.recordType === RecordType.AREA,
        }).unwrap();
      } catch (err) {
        console.error("❌ Record creation failed:", err);
        if (err.data?.issues) {
          setIssues(err.data.issues);
          return;
        }

        throw err;
      }

      const newRecord = response.data;

      if (newRecord && formData.files && formData.files.length > 0) {
        const res = await uploadFiles(newRecord.id, formData.files);
        if (!res) {
          uploadFailed = true;
        }
      }

      if (uploadFailed) {
        // remove record
        await deleteRecord(newRecord.id).unwrap();
        return;
      }
    }

    if (data.isMeasure) {
      try {
        await createMeasure({
          projectId: project.id,
          dimension: mode,
          name: data.name,
          type: measure.type,
          data: measure.points,
        }).unwrap();
      } catch (err) {
        if (err.data?.issues) {
          setIssues(err.data.issues);
          return;
        }

        throw err;
      }

      if (initialData.isFromRecord) {
        // need to add new measure
        dispatch(removeNonStoredRecord(record.id));
      } else {
        dispatch(removeNonStoredMeasure(measure.id));
      }
    } else {
      if (record) {
        dispatch(removeNonStoredRecord(record.id));
      } else {
        dispatch(removeNonStoredMeasure(measure.id));
      }
    }

    onClose();
  }, [
    data,
    formData,
    project,
    createRecord,
    initialData,
    createMeasure,
    dispatch,
    mode,
    deleteRecord,
    onClose,
    uploadFiles,
  ]);

  useEffect(() => {
    if (!isOpen) {
      isInitialSetup.current = true;
      return;
    }
    if (!project) return;

    if (!isInitialSetup.current) return;
    isInitialSetup.current = false;

    const { measure } = initialData;

    const types = filterRecordTypesForToolMode(
      // filter record types based on tool mode
      project.allowedRecordTypes,
      measure.type,
    );

    const filteredTypes = types.filter(
      (type) => type !== RecordType.SOLAR_PANEL,
    );
    const recordType = filteredTypes[0];

    setRecordTypeOptions(
      project.allowedRecordTypes
        .map((recordType) => ({
          value: recordType,
          label: t(`form.${recordType}`),
          disabled: !filteredTypes.includes(recordType),
        }))
        .filter((type) => type.value !== RecordType.SOLAR_PANEL),
    );

    if (filteredTypes.length === 0) {
      // if no record types are allowed, create measure
      setCheckRecordVisible(false);
      setData({
        name: measure.name,
        recordType: null,
        isRecord: false,
        isMeasure: true,
      });
    } else {
      // if record types are allowed, create record or measure
      setCheckRecordVisible(true);
      setData({
        name: measure.name,
        recordType,
        isRecord: initialData.isFromRecord,
        isMeasure: !initialData.isFromRecord,
      });
      setFormData({
        points: measure.points,
      });
    }
  }, [isOpen, project, initialData, t]);

  useEffect(() => {
    if (isOpen) {
      setIssues(null);
      clearUploadError();
    }
  }, [isOpen, clearUploadError]);

  const handleChangeName = (value) => {
    setData({ ...data, name: value });
  };

  const canSave = data.isRecord || data.isMeasure;

  if (!isOpen) return null;

  return (
    <DialogWrapper onClick={handleCancel}>
      <div
        className="relative bg-white rounded-xl w-[671px] pt-[65px] pb-[53px]"
        onClick={(e) => e.stopPropagation()}
      >
        <CloseButton onClick={handleCancel} />
        <div className="flex flex-col gap-[30px] items-center px-[20px]">
          <DialogTitle title={t("createRecordDialog.title")} />

          <div className="flex flex-row justify-between gap-[40px]">
            <div className="flex flex-row justify-between items-end w-[313px]">
              <TextInput
                inputClassName="h-[38px] w-[313px]"
                label={t("createRecordDialog.name")}
                value={data.name ?? ""}
                onChange={handleChangeName}
                extraError={getErrorForPath(issues, ["name"])}
              />
            </div>

            {checkRecordVisible && (
              <div className="flex flex-col gap-[24px] w-[176px]">
                <CheckboxInput
                  label={t("createRecordDialog.isRecord")}
                  checked={data.isRecord}
                  disabled={canEdit === false}
                  onChange={(checked) =>
                    setData({ ...data, isRecord: checked })
                  }
                />

                <CheckboxInput
                  label={t("createRecordDialog.isMeasure")}
                  checked={data.isMeasure}
                  disabled={canEdit === false}
                  onChange={(checked) =>
                    setData({ ...data, isMeasure: checked })
                  }
                />
              </div>
            )}
          </div>

          {data?.isRecord && (
            <div
              className="flex flex-col gap-[24px]
            overflow-y-auto overflow-x-hidden max-h-[500px]
            bg-bg1 rounded-[30px] py-[30px] w-[524px] px-[40px]
          "
            >
              <TogglesSelect
                label={t("form.recordType")}
                options={recordTypeOptions}
                selectedOption={data.recordType}
                onChange={({ value }) => {
                  setData({ ...data, recordType: value });
                  setFormData((prev) => ({
                    points: prev.points,
                    isRecord: prev.isRecord,
                    isMeasure: prev.isMeasure,
                  }));
                }}
              />

              {data.recordType === RecordType.FAULT && (
                <FVEForm
                  data={formData}
                  onChange={setFormData}
                  fillDefault={true}
                  issues={issues}
                />
              )}

              {data.recordType === RecordType.CEMETERY && (
                <GraveYardForm
                  data={formData}
                  onChange={setFormData}
                  issues={issues}
                />
              )}

              {data.recordType === RecordType.PARK && (
                <ParksAndGardensForm
                  data={formData}
                  onChange={setFormData}
                  issues={issues}
                />
              )}

              {data.recordType === RecordType.AREA && (
                <FacilityForm
                  data={formData}
                  onChange={setFormData}
                  issues={issues}
                />
              )}

              {data.recordType === RecordType.GENERIC && (
                <GeneralForm
                  data={formData}
                  onChange={setFormData}
                  issues={issues}
                />
              )}

              {data.recordType === RecordType.SERVICE && (
                <ServiceForm
                  data={formData}
                  onChange={setFormData}
                  issues={issues}
                />
              )}
            </div>
          )}

          <div className="px-[80px] w-full">
            {isUploading && <LoadingBar progress={uploadProgress * 100} />}
          </div>

          {uploadError && (
            <div className="text-red-500 text-center text-xs">
              {uploadError}
            </div>
          )}

          <div className="flex flex-row justify-between gap-[64px] mt-[20px]">
            <TextButton
              className={"h-[56px]"}
              secondary={true}
              label={t("createRecordDialog.cancel")}
              onClick={handleCancel}
            />
            <TextButton
              disabled={!canSave}
              isLoading={isLoading}
              className={"h-[56px]"}
              label={t("createRecordDialog.create")}
              onClick={handleSubmit}
            />
          </div>
        </div>
      </div>
    </DialogWrapper>
  );
};

export default CreateRecordDialog;
